import type { Project } from '@plane/shared';

import { useProjects } from '@/modules/projects/hooks/use-projects';
import { apiBaseUrl } from '@/services/http-client';

/**
 * Galeria de obras entregues.
 *
 * Conteúdo e identidade vêm do protótipo em `legacy/static-site`; o que muda é
 * a origem, que agora é o CMS. O trilho horizontal com rolagem por arrasto é
 * parte do desenho — as fotos são o produto da marcenaria, e a faixa larga dá
 * a elas mais espaço do que uma grade caberia.
 */
export function ProjectsSection() {
  const { data, isPending, isError } = useProjects();

  return (
    <section id="projetos" className="py-[clamp(64px,9vw,120px)]">
      <div className="mx-auto max-w-[1360px] px-6">
        <header className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs tracking-[0.18em] text-brass uppercase">
              <span aria-hidden="true" className="inline-block h-px w-6 bg-brass" />
              03 — Projetos
            </p>

            <h2 className="mt-4 font-display text-[clamp(1.9rem,4vw,3rem)] leading-[1.1] text-balance">
              Entregues, medidos
              <br />e <em className="text-brand-600">fotografados</em> em casa.
            </h2>
          </div>

          <p className="max-w-[42ch] text-sm text-ink-soft">
            Nada de render. Tudo aqui embaixo saiu da nossa fábrica e foi registrado depois da
            montagem, com o morador já usando.
          </p>
        </header>
      </div>

      <Conteudo projetos={data} carregando={isPending} falhou={isError} />
    </section>
  );
}

function Conteudo({
  projetos,
  carregando,
  falhou,
}: {
  projetos: Project[] | undefined;
  carregando: boolean;
  falhou: boolean;
}) {
  const moldura = 'mx-auto mt-10 max-w-[1360px] px-6';

  if (carregando) {
    return (
      <div className={moldura} role="status" aria-live="polite">
        {/* Esqueleto com a mesma proporção dos cards, para o layout não pular. */}
        <div className="flex gap-[clamp(16px,2.2vw,30px)] overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-[clamp(260px,30vw,420px)] shrink-0">
              <div className="aspect-[4/3] w-full bg-sand" />
              <div className="mt-3 h-4 w-2/3 bg-sand" />
              <div className="mt-2 h-3 w-full bg-sand" />
            </div>
          ))}
        </div>
        <span className="sr-only">Carregando projetos…</span>
      </div>
    );
  }

  // Falha de rede não pode derrubar a página inteira: o restante do site é
  // conteúdo estático e continua útil. A seção some em silêncio.
  if (falhou || !projetos || projetos.length === 0) return null;

  return (
    <div
      className="mt-10 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ scrollSnapType: 'x proximity' }}
      tabIndex={0}
      role="region"
      aria-label="Galeria de projetos — role para o lado"
    >
      <ul className="flex w-max gap-[clamp(16px,2.2vw,30px)] px-6 lg:px-[max(1.5rem,calc((100vw-1360px)/2+1.5rem))]">
        {projetos.map((projeto) => (
          <Cartao key={projeto.id} projeto={projeto} />
        ))}
      </ul>
    </div>
  );
}

function Cartao({ projeto }: { projeto: Project }) {
  const capa = projeto.images[0];

  return (
    <li className="w-[clamp(260px,30vw,420px)] shrink-0" style={{ scrollSnapAlign: 'start' }}>
      <figure className="flex h-full flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-sand">
          {capa ? (
            <img
              src={`${apiBaseUrl}${capa.url}`}
              alt={capa.alt}
              width={capa.width}
              height={capa.height}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[1100ms] ease-plane-slow hover:scale-[1.03] motion-reduce:transition-none motion-reduce:hover:scale-100"
            />
          ) : (
            <div aria-hidden="true" className="h-full w-full" />
          )}

          {projeto.measurement && (
            <span className="absolute right-3.5 bottom-3.5 left-3.5 flex items-center gap-2 text-[0.64rem] tracking-[0.18em] text-bone uppercase">
              <span aria-hidden="true" className="h-px flex-1 bg-bone/70" />
              <b className="font-normal">{projeto.measurement}</b>
              <span aria-hidden="true" className="h-px flex-1 bg-bone/70" />
            </span>
          )}
        </div>

        <figcaption className="mt-3 flex flex-col gap-1.5">
          <h3 className="font-display text-lg">{projeto.title}</h3>
          <p className="text-sm text-ink-soft">{projeto.specs}</p>
          <span className="text-[0.64rem] tracking-[0.18em] text-ink-soft uppercase">
            {projeto.place} · {projeto.year}
          </span>
        </figcaption>
      </figure>
    </li>
  );
}
